import {useMemo} from 'react'
import {create} from 'zustand'

import type {BrowserInfo} from '@/lib/browser'
import {detectBrowser} from '@/lib/browser'
import {detectAll} from '@/lib/detection'
import {findUnknownGlobals, type UnknownGlobal} from '@/lib/introspect'
import type {ApiEntry, CategoryId, RuntimeStatus} from '@/lib/types'

export type SortMode = 'category' | 'baseline' | 'alphabetic' | 'hierarchy'

// Hydrate initial filter/sort/search state from the URL synchronously so we
// don't flash defaults on reload.
function urlInitial() {
  if (typeof window === 'undefined') return null
  const p = new URLSearchParams(window.location.search)
  return {
    selectedId: p.get('api') || null,
    search: p.get('q') || '',
    cat: p.get('cat'),
    onlySupported: p.get('supported') === '1',
    onlyWithDemos: p.get('demos') === '1',
    sort: p.get('sort') as SortMode | null,
  }
}

import catalogJson from '@/data/catalog.json'

const catalog = catalogJson as {
  entries: ApiEntry[]
  relationships: {from: string; to: string; label?: string}[]
  bcdVersion: string
  webFeaturesVersion: string
  generatedAt: string
}

interface State {
  // catalog
  entries: ApiEntry[]
  relationships: {from: string; to: string; label?: string}[]
  bcdVersion: string
  webFeaturesVersion: string

  // runtime state
  selectedId: string | null
  runtime: Record<string, RuntimeStatus>
  unknown: UnknownGlobal[]
  unknownPanelOpen: boolean
  browser: BrowserInfo | null

  // filters
  search: string
  visibleCategories: Set<CategoryId>
  onlySupported: boolean
  onlyWithDemos: boolean
  sortMode: SortMode

  // actions
  initialize: () => Promise<void>
  select: (id: string | null) => void
  setUnknownPanelOpen: (v: boolean) => void
  setSearch: (s: string) => void
  toggleCategory: (id: CategoryId) => void
  setAllCategories: (visible: boolean) => void
  setVisibleCategories: (cats: Iterable<CategoryId>) => void
  setOnlySupported: (v: boolean) => void
  setOnlyWithDemos: (v: boolean) => void
  setSortMode: (m: SortMode) => void
  resetFilters: () => void
}

const allCategories = new Set<CategoryId>(catalog.entries.map((e) => e.category))
const categoriesInCatalog = Array.from(allCategories)

const _urlInit = urlInitial()
const _initialCategories = _urlInit?.cat
  ? new Set<CategoryId>(
      _urlInit.cat
        .split(',')
        .map((s) => s.trim())
        .filter((s): s is CategoryId => categoriesInCatalog.includes(s as CategoryId))
    )
  : allCategories
const _validSortModes: ReadonlySet<SortMode> = new Set([
  'category',
  'baseline',
  'alphabetic',
  'hierarchy',
])
const _initialSort: SortMode =
  _urlInit?.sort && _validSortModes.has(_urlInit.sort) ? _urlInit.sort : 'category'

export const useStore = create<State>((set, get) => ({
  entries: catalog.entries,
  relationships: catalog.relationships,
  bcdVersion: catalog.bcdVersion,
  webFeaturesVersion: catalog.webFeaturesVersion,
  selectedId: _urlInit?.selectedId ?? null,
  runtime: {},
  unknown: [],
  unknownPanelOpen: false,
  browser: null,
  search: _urlInit?.search ?? '',
  visibleCategories: _initialCategories,
  onlySupported: _urlInit?.onlySupported ?? false,
  onlyWithDemos: _urlInit?.onlyWithDemos ?? false,
  sortMode: _initialSort,

  initialize: async () => {
    const runtime = detectAll(catalog.entries)
    const runtimeKeys = new Set(catalog.entries.map((e) => e.runtimeKey))
    // Many catalog entries use the instance accessor as their runtime key
    // (`navigator.gpu`, `window.indexedDB`, `navigator.bluetooth`, …) but
    // browsers also expose the *constructor* on `window` under the BCD
    // identifier name (`window.GPU`, `window.IDBDatabase`, `window.Bluetooth`).
    // Register both as known so the introspector doesn't double-count.
    for (const e of catalog.entries) {
      const last = e.id.split('.').pop()
      if (last && /^[A-Z]/.test(last) && !last.endsWith('_static')) {
        runtimeKeys.add(`window.${last}`)
      }
    }
    const unknown = findUnknownGlobals(runtimeKeys)
    const browser = await detectBrowser()
    set({runtime, unknown, browser})
  },

  select: (id) => set({selectedId: id}),
  setUnknownPanelOpen: (v) => set({unknownPanelOpen: v}),
  setSearch: (s) => set({search: s}),
  toggleCategory: (id) => {
    const current = get().visibleCategories
    const allSize = allCategories.size

    // "Click filter to focus" pattern. If we're currently showing every
    // category (i.e. no filter is active), clicking a chip means "only
    // this one" — it doesn't make sense to subtract from the implicit
    // "all" set.
    if (current.size === allSize) {
      set({visibleCategories: new Set([id])})
      return
    }

    // Otherwise it's a normal toggle in the active filter set.
    const next = new Set(current)
    if (next.has(id)) next.delete(id)
    else next.add(id)

    // If toggling just emptied the set, revert to showing all — that's the
    // user's way of saying "never mind, drop the filter". They can still
    // get the empty state via the "none" bulk button.
    if (next.size === 0) {
      set({visibleCategories: new Set(allCategories)})
      return
    }
    set({visibleCategories: next})
  },
  setAllCategories: (visible) => {
    set({visibleCategories: visible ? new Set(allCategories) : new Set()})
  },
  setVisibleCategories: (cats) => {
    set({visibleCategories: new Set(cats)})
  },
  setOnlySupported: (v) => set({onlySupported: v}),
  setOnlyWithDemos: (v) => set({onlyWithDemos: v}),
  setSortMode: (m) => set({sortMode: m}),
  resetFilters: () =>
    set({
      search: '',
      visibleCategories: new Set(allCategories),
      onlySupported: false,
      onlyWithDemos: false,
      sortMode: 'category',
    }),
}))

/**
 * Derive filtered entry list.
 *
 * IMPORTANT: read primitive store fields, then derive with `useMemo`. Returning
 * a fresh `.filter()` result directly from a Zustand selector triggers an
 * infinite re-render loop (React's getSnapshot cache check fails on every
 * call because the array reference is new).
 */
export function useFilteredEntries() {
  const entries = useStore((s) => s.entries)
  const runtime = useStore((s) => s.runtime)
  const search = useStore((s) => s.search)
  const visibleCategories = useStore((s) => s.visibleCategories)
  const onlySupported = useStore((s) => s.onlySupported)
  const onlyWithDemos = useStore((s) => s.onlyWithDemos)

  return useMemo(() => {
    const term = search.trim().toLowerCase()
    return entries.filter((e) => {
      if (!visibleCategories.has(e.category)) return false
      if (onlySupported && !runtime[e.id]?.supported) return false
      if (onlyWithDemos && !e.hasDemo) return false
      if (term) {
        const hay = `${e.title} ${e.id} ${e.description}`.toLowerCase()
        if (!hay.includes(term)) return false
      }
      return true
    })
  }, [entries, runtime, search, visibleCategories, onlySupported, onlyWithDemos])
}
